import { useEffect, useState } from "react";
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import { Download, LockKeyhole } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL, api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

export function PayPalCheckout({ product }) {
  const [config, setConfig] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/paypal/config").then(({ data }) => setConfig(data)).catch(() => setConfig({ configured: false }));
  }, []);

  if (!product.has_file) {
    return (
      <div data-testid="delivery-pending-panel" className="rounded-3xl border border-amber-200 bg-amber-50 p-5 text-sm leading-6 text-amber-900">
        This product is visible, but its delivery file has not been attached yet.
      </div>
    );
  }

  if (!config) {
    return <div data-testid="checkout-loading" className="rounded-3xl bg-[#F4F0EA] p-5 text-sm">Preparing secure checkout…</div>;
  }

  if (!config.configured) {
    return (
      <div data-testid="paypal-credentials-needed" className="rounded-3xl border border-[#E6E2DA] bg-white p-5">
        <div className="flex items-center gap-3 font-heading font-bold"><LockKeyhole className="h-5 w-5 text-amber-700" /> PayPal checkout is ready to connect</div>
        <p className="mt-3 text-sm leading-6 text-[#525866]">Add the PayPal app credentials on the server to activate live payment buttons and automatic delivery.</p>
        <Button disabled className="mt-4 w-full rounded-full" data-testid="paypal-disabled-button">Checkout pending credentials</Button>
      </div>
    );
  }

  return (
    <div data-testid="paypal-checkout-panel" className="rounded-3xl border border-[#E6E2DA] bg-white p-5">
      {downloadUrl ? (
        <div className="text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#3F5E4D]/10 text-[#3F5E4D]"><Download className="h-6 w-6" /></div>
          <h3 className="mt-4 font-heading text-xl font-bold">Payment confirmed</h3>
          <p className="mt-2 text-sm text-[#525866]">Your secure download is ready.</p>
          <Button asChild className="mt-5 w-full rounded-full bg-[#3F5E4D] text-white hover:bg-[#2f493c]" data-testid="download-product-button">
            <a href={`${BACKEND_URL}${downloadUrl}`}>Download your product</a>
          </Button>
        </div>
      ) : (
        <PayPalScriptProvider options={{ "client-id": config.clientId, currency: product.currency || "USD", intent: "capture" }}>
          <PayPalButtons
            data-testid="paypal-payment-buttons"
            style={{ layout: "vertical", shape: "pill", label: "paypal" }}
            disabled={busy}
            createOrder={async () => {
              setBusy(true);
              try {
                const { data } = await api.post("/checkout/paypal/create", { product_id: product.id });
                return data.id;
              } catch (error) {
                toast.error(formatApiError(error, "Unable to start checkout"));
                throw error;
              } finally {
                setBusy(false);
              }
            }}
            onApprove={async (data) => {
              setBusy(true);
              try {
                const response = await api.post(`/checkout/paypal/${data.orderID}/capture`, {});
                setDownloadUrl(response.data.download_url);
                toast.success("Payment confirmed");
              } catch (error) {
                toast.error(formatApiError(error, "Unable to confirm payment"));
              } finally {
                setBusy(false);
              }
            }}
            onError={() => toast.error("PayPal checkout could not be completed")}
          />
        </PayPalScriptProvider>
      )}
    </div>
  );
}
